// Display helpers for published reviews (homepage carousel + /review).
import type { CSSProperties } from "react"

// Keeps homepage quotes the same size as the curated ones. The cap is the
// length of the Net Vir Pret testimonial — the longest curated quote and the
// size the carousel was designed around.
export const QUOTE_MAX_CHARS = 273

// Shortens a review by keeping whole sentences from the start, in order, and
// marking the cut with an ellipsis (the curated quotes use the same "…"
// convention). It never rewrites or paraphrases — a published quote must stay
// the reviewer's own words; the full text remains on /review.
export function excerptQuote(text: string, max = QUOTE_MAX_CHARS): string {
  const clean = text.replace(/\s+/g, " ").trim()
  if (clean.length <= max) return clean

  const sentences = clean.match(/[^.!?…]+(?:[.!?…]+["'”’)]*|$)\s*/g) ?? [clean]
  let kept = ""
  for (const s of sentences) {
    if ((kept + s).trimEnd().length + 1 > max) break
    kept += s
  }
  kept = kept.trim()

  // First sentence alone is over the cap — cut at the last whole word.
  if (!kept) {
    const cut = clean.slice(0, max - 1)
    const lastSpace = cut.lastIndexOf(" ")
    kept = lastSpace > max / 2 ? cut.slice(0, lastSpace) : cut
  }
  return `${kept.replace(/[\s.,;:…]+$/, "")}…`
}

// The reviewer's company website, which their name links to. Accepts what
// people type ("floridathrifty.co.za", "www.x.com/shop") and returns a full
// http(s) URL, or null for anything that isn't a plain public web address.
export function normalizeWebsite(raw: string | null | undefined): string | null {
  const v = (raw ?? "").trim()
  if (!v || v.length > 200 || /\s/.test(v)) return null
  try {
    const u = new URL(/^[a-z][a-z0-9+.-]*:/i.test(v) ? v : `https://${v}`)
    if (u.protocol !== "https:" && u.protocol !== "http:") return null
    if (u.username || u.password || !/^[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(u.hostname)) return null
    return u.href
  } catch {
    return null
  }
}

// Anyone can submit a review, so links in them are marked as user-supplied and
// pass no ranking credit.
export const REVIEW_LINK_REL = "nofollow ugc noopener noreferrer"

// A quiet underline so the name reads as a link without shouting.
export const REVIEW_LINK_STYLE: CSSProperties = {
  color: "inherit",
  textDecoration: "underline",
  textDecorationColor: "rgba(255,255,255,0.35)",
  textUnderlineOffset: 3,
}

// Every review is attributed the same way (Monique's structure, 2026-09-11):
// the person's name, then "Role, Company - what the business does".
//   Nataniel Pokwas
//   Administrative Clerk, Net Vir Pret - Community of Practice for Social Change
// `subtitle` is null when there is nothing to add — callers fall back to their
// own line (e.g. the stars).
export function reviewAttribution(r: { name: string; company?: string | null; role?: string | null; about?: string | null }): {
  title: string
  subtitle: string | null
} {
  const who = [r.role, r.company].filter(Boolean).join(", ")
  return { title: r.name, subtitle: [who, r.about].filter(Boolean).join(" - ") || null }
}
